export type TDragPosition = { x: number; y: number };

const DEFAULT_DRAG_THRESHOLD = 5;

type TDrag = {
  origin: TDragPosition;
  pointer: TDragPosition;
};

/**
 * Tracks one drag for a handle wired to `pointerdown`, `pointermove` and `pointerup`.
 * Reports where the dragged thing should sit, and whether a press turned out to be a tap.
 * So the caller stays free to clamp the result however it needs to.
 */
export class PointerDragTracker {
  #drag: TDrag | null = null;
  #hasPassedThreshold = false;

  constructor(private readonly dragThreshold = DEFAULT_DRAG_THRESHOLD) {}

  startDrag(event: PointerEvent, origin: TDragPosition) {
    (event.target as HTMLElement).setPointerCapture(event.pointerId);

    this.#drag = { origin, pointer: { x: event.clientX, y: event.clientY } };
    this.#hasPassedThreshold = false;
  }

  track(event: PointerEvent): TDragPosition | null {
    if (!this.#drag) return null;

    const { origin, pointer } = this.#drag;
    const deltaX = event.clientX - pointer.x;
    const deltaY = event.clientY - pointer.y;

    if (!this.#hasPassedThreshold && Math.hypot(deltaX, deltaY) < this.dragThreshold) return null;

    this.#hasPassedThreshold = true;
    return { x: origin.x + deltaX, y: origin.y + deltaY };
  }

  endDrag(): { wasTap: boolean } {
    const wasTap = !!this.#drag && !this.#hasPassedThreshold;

    this.#drag = null;
    this.#hasPassedThreshold = false;

    return { wasTap };
  }
}
